/**
 * Messages for failed API calls that tell people what happened.
 *
 * The generated SDK throws the parsed error body (`{ error: { code, message } }`)
 * for HTTP errors, and a `TypeError` when the request got no response.
 */
import { msg } from "@lit/localize";

type ErrorBody = { error?: { code?: unknown; message?: unknown } } | null;

/** The server's error code (e.g. "forbidden"), when the error carries one. */
export function apiErrorCode(err: unknown): string | undefined {
  const code = (err as ErrorBody)?.error?.code;
  return typeof code === "string" ? code : undefined;
}

/** True when the request got no response (offline, DNS, server down). */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError;
}

/**
 * The message to show for a failed action: `forbidden` explains a permission
 * refusal for this action, input problems use the server's own wording, and
 * anything else gets `fallback`.
 */
export function apiErrorMessage(
  err: unknown,
  messages: { forbidden: string; fallback: string },
): string {
  if (isNetworkError(err)) {
    return msg("Can't reach the server. Check your connection and try again.");
  }
  switch (apiErrorCode(err)) {
    case "forbidden":
      return messages.forbidden;
    case "rate_limited":
      return msg("Too many attempts. Wait a moment and try again.");
    // Story rules (no move-back, last task needs confirmed test cases): the
    // server's message already explains what to do, in the user's language.
    case "status_revert_forbidden":
    case "story_tests_pending":
    case "validation_error":
    case "conflict":
    case "not_found": {
      const text = (err as ErrorBody)?.error?.message;
      return typeof text === "string" && text ? text : messages.fallback;
    }
    default:
      return messages.fallback;
  }
}
